import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { Company, searchQueryParams } from "../helpers/shared-interfaces";
import { Col, Row, Tag, message } from "antd";
import Card from "../components/Card";
import Table, { ColumnType } from "antd/lib/table";
import debounce from "lodash.debounce";
import { request , showTotalPagination } from "../helpers/utilities";
import { PageToolbar } from "../components/PageToolbar";
import * as Sentry from "@sentry/browser";
import queryString from "query-string";
import { GET_ARCHIVED_COMPANIES } from "../gql/companies";
import { extractPageFromQuery } from "../components/TableFilters";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { generateDisplayErrorMessage } from "../helpers/utilities";



interface State {
  isLoading: boolean;
  page: number;
  pageSize: number;
  q: string;
  companies: Company[];
  companiesCount: number;
}

function getCompanyColumns<T extends Company = Company>(
  requests: Company[],
): ColumnType<T>[] {
  return [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "2%",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "PetraID",
      dataIndex: "petraId",
      key: "petraId",
      width: "10%",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "Name",
      dataIndex: "company",
      key: "company",
      width: "13%",
      render(text: string, record: Company): JSX.Element {
        return <span>{record.name}</span>;
      },
    },
    {
      title: "Email",
      dataIndex: "companyEmails",
      key: "companyEmails",
      width: "20%",
      render(text: string, record: Company): JSX.Element {
        let email: string = record?.companyEmails?.[0]?.email?.address ?? "";
        return (
          <span>
            {email ? (
              <div key="cEmail">
                <span>{email}</span>
              </div>
            ) : (
              <Tag color="red" key="cEmailNotAssigned">
                No Email
              </Tag>
            )}
          </span>
        );
      },
    },
    {
      title: "Phone",
      dataIndex: "companyPhones",
      key: "companyPhones",
      width: "15%",
      render(text: string, record: Company): JSX.Element {
        let phone: string = record?.companyPhones?.[0]?.phone?.number ?? "";
        return (
          <span>
            {phone ? (
              <div key="cPhone">
                <span>{phone}</span>
              </div>
            ) : (
              <Tag color="red" key="cPhoneNotAssigned">
                No Mobile Number
              </Tag>
            )}
          </span>
        );
      },
    },
    {
      title: "Contact Person",
      dataIndex: "contactPerson",
      key: "contactPerson",
      width: "15%",
      render(text: string, record: Company): JSX.Element {
        let contactPerson: string | undefined = undefined;
        contactPerson =
          record.contactPersonValue && record.contactPersonValue.split(",")[0];
        return (
          <span>
            {contactPerson ? (
              <div key="cPhone">
                <span>{contactPerson}</span>
              </div>
            ) : (
              <Tag color="red" key="contactPersonNotAvailable">
                No Contact Person
              </Tag>
            )}
          </span>
        );
      },
    },
  ];
}

export default class ArchivedCompanies extends React.Component<
  RouteComponentProps,
  State
> {
  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      companies: [],
      companiesCount: 0,
      page: 1,
      pageSize: 25,
      q: "",
    };

    this.nextPage = this.nextPage.bind(this);
  }

  async componentDidMount(): Promise<void> {
    this.mergeWithLocalState();
    await this.triggerImmediateSearch();
  }

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState(
      {
        isLoading: true,
        page,
        pageSize,
      },
      async () => {
        await this.triggerImmediateSearch();
      },
    );
  }

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
  this.setState({ q: event.target.value, page: 1 });
    await this.triggerDelayedSearch();
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  triggerImmediateSearch = async (): Promise<void> => {
    const { q, page, pageSize } = this.state;
    this.setState({ isLoading: true });

    try {
      const { data: { data } }: any = await request({
        query: GET_ARCHIVED_COMPANIES,
        variables: { page, q, numberPerPage: pageSize },
      });
      
      this.syncBrowserQueryString();
      this.setState({
        companies: data?.archivedCompanySearch?.results,
        companiesCount: data?.archivedCompanySearch?.count,
      });
    } catch (error) {
      message.error(`Failed To Load Companies - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  extractQueryParamsFromUrl = (): searchQueryParams => {
    const parsed = queryString.parse(window.location.search);
    return JSON.parse(JSON.stringify(parsed)) as searchQueryParams;
  };

  mergeWithLocalState = async (): Promise<void> => {
    const parsed = this.extractQueryParamsFromUrl();
    let q = "";
    let page = 1;

    if (parsed.q) {
      q = parsed.q;
    }

    if (parsed.page) {
      page = extractPageFromQuery(parsed.page);
    }

    this.setState({
      q,
      page,
    });
  };

  syncBrowserQueryString = (): void => {
    const { q, page } = this.state;
    const queryStrings = [];
    if (q) {
      queryStrings.push(`q=${q}`);
    }
    queryStrings.push(`page=${page}`);

    const queryString = `?${queryStrings.join("&")}`;

    this.props.history.replace(queryString);
  };

  render(): JSX.Element | null {
    const { isLoading, q, companies, companiesCount, page, pageSize } =
      this.state;

    const resultsWithKeys: Company[] = companies.map(
      (result: Company): Company => {
        return {
          ...result,
          key: result.id,
        };
      },
    );

    return (
        <Card>
          <div className="customers-container">
            <PageToolbar
              searchPlaceholder="Search by PetraID, Email, Phone, Name, StaffId, Contact Person ..."
              searchValue={q}
              onSearchChange={this.handleSearchInput}
              totalCount={companiesCount}
              currentDataCount={companies.length}
              page={page}
              pageSize={pageSize}
              modelName="archived companies"
            />

            <Row>
              <Col span={24}>
                <Table
                  loading={isLoading}
                  scroll={{ x: 400 }}
                    columns={getCompanyColumns(companies)}
                    dataSource={resultsWithKeys}
                    onRow={(record) => ({
                      onClick: () => {
                        this.props.history.push({
                          pathname: `/archived-companies/${record.id}`,
                          state: { type: "company", requests: companies },
                        }
                        )
                      },
                      style: { cursor: 'pointer' }

                    })}
                    pagination={{
                      current: page,
                      defaultPageSize: pageSize,
                      total: companiesCount,
                      onChange: this.nextPage,
                      showTotal: showTotalPagination,
                    }}
                />
              </Col>
            </Row>
          </div>
        </Card>
    );
  }
}
